import { Container, Loader, LocaleProvider } from '@acast-tech/decibel';
import PageErrorBoundary from './PageErrorBoundary';
import { useDocumentTitle } from '@acast-tech/product-suite-app-shell';
import { Navigate, Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom';
import SubNavigation from './SubNavigation';
import NoAccessMessage from './NoAccessMessage';
import useLaunchDarklyContext from '@/hooks/useLaunchDarklyContext';
import { DerivedNetworkProvider } from '@/context';
import { useDerivedNetwork, useDerivedNetworks, usePageMetadata } from '@/hooks';
import { useAuth } from '@acast-tech/auth-client';
import { useUserNetworksQuery } from '@/services/userApiQueries';
import { PageType } from '@/types';
import { APP_TO_DEFAULT_PAGE_MAP, CONTENT_PERFORMANCE_PAGE, PAGE_TYPES } from '@/utils/constants';
import { RouteBuilder } from '@/utils/routeUtils';
import { useCallback, useMemo } from 'react';

interface PageWrapperProps {
  /**
   * Where the selected network is read from: the legacy `/:networkId/...` path param (`'path'`,
   * default) or the `?networks=` query param (`'query'`, used by the multi-network content
   * performance route). In query mode the primary network (`networks[0]`) drives sub-nav, doc
   * title, LaunchDarkly context, authorization and redirects.
   */
  networkSource?: 'path' | 'query';
}

/**
 * This component is used to wrap all revenue and insights dashboard pages and handles all logic needed, such as:
 * - Page authorization
 * - Network selection
 * - Page type selection
 * - Page navigation
 * - LaunchDarkly context
 * @param networkSource - Where the selected network is read from: the legacy `/:networkId/...` path param (`'path'`), or the `?networks=` query param (`'query'`).
 * @returns {JSX.Element}
 * @example
 * <PageWrapper networkSource="path" />
 * <PageWrapper networkSource="query" />
 */
const PageWrapper = ({ networkSource = 'path' }: PageWrapperProps) => {
  const auth = useAuth();
  const { userId, isImpersonating } = auth;
  const { networkId } = useParams();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const isQueryMode = networkSource === 'query';

  // Builds a page URL for redirects. Content performance uses the query-param URL
  // (`/content-performance?networks=<id>`); everything else stays path-based.
  const buildPagePath = useCallback((targetNetworkId: string, pageType: PageType): string => {
    if (pageType === 'content-performance') {
      return RouteBuilder.contentPerformance({ networkIds: [targetNetworkId], showIds: [] });
    }
    return `/${targetNetworkId}/${pageType}`;
  }, []);

  // Fetch ALL networks (both insights and revenue)
  const { data: allNetworks, isPending } = useUserNetworksQuery(userId, isImpersonating);
  const { app, page } = usePageMetadata(allNetworks);

  // Get the networks for the current app type
  const networks = allNetworks?.[app];

  // Path mode derives a single network from `/:networkId/...`; query mode derives the full list from
  // `?networks=`. Both hooks run unconditionally; only the active source's result is used.
  const pathSelectedNetwork = useDerivedNetwork(networks, isPending);
  const { selectedNetwork: querySelectedNetwork, selectedNetworks: querySelectedNetworks } =
    useDerivedNetworks(networks, isPending);

  // The primary network drives sub-nav, doc title, LaunchDarkly context and authorization.
  const selectedNetwork = isQueryMode ? querySelectedNetwork : pathSelectedNetwork;
  const selectedNetworks = isQueryMode ? querySelectedNetworks : [pathSelectedNetwork];

  useDocumentTitle(selectedNetwork ? selectedNetwork.name : '');
  const { isContextReady } = useLaunchDarklyContext({
    selectedNetwork,
    userId,
    isImpersonating,
    allNetworks,
  });

  const isLoading = Boolean(isPending || (!isContextReady && selectedNetwork?.id));

  const networksParam = searchParams.get('networks');

  // Every `revenue` role also grants `analytics` (per the Roles API), so insights networks cover
  // all users.
  const defaultNetworkId = allNetworks?.insights[0]?.id;

  // Unknown networks fall through to NoAccessMessage rather than being redirected.
  const isUrlNetworkKnown = Boolean(
    allNetworks?.revenue.some((network) => network.id === networkId) ||
      allNetworks?.insights.some((network) => network.id === networkId)
  );

  // `selectedNetwork` is undefined on pages where the user has no network for that app (e.g. an
  // analyst-only user on earnings), so tab links can't rely on it alone.
  const navigationNetworkId =
    selectedNetwork?.id ?? (isUrlNetworkKnown ? networkId : defaultNetworkId);

  // Determine redirect target if user needs to be redirected
  const redirectTarget = useMemo(() => {
    if (isPending || isLoading || !allNetworks) return null;

    // Query mode resolves its network(s) from `?networks=`, so the path-based logic below
    // (which keys off `networkId`) does not apply.
    if (isQueryMode) {
      // No `networks` param at all (e.g. a bare `/content-performance` entry) — seed it with the
      // first available network, mirroring the path-mode root redirect. A present-but-unresolvable
      // param falls through to NoAccessMessage instead, matching the path-mode unknown-network case.
      if (!networksParam && networks && networks.length > 0) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('networks', networks[0].id);
        return `/content-performance?${nextParams.toString()}`;
      }

      return null;
    }

    // Case 1: Root route - redirect to Content Performance.
    if (!networkId) {
      if (defaultNetworkId) {
        return buildPagePath(defaultNetworkId, CONTENT_PERFORMANCE_PAGE);
      }
      return null; // No networks available - will show NoAccessMessage
    }

    // Case 2: networkId present but no page segment — usePageMetadata already
    // resolved the correct app via network-list lookup, so redirect to its default page.
    const pathSegments = pathname.split('/').filter(Boolean);
    const hasPageType = PAGE_TYPES.some((type) => pathSegments.includes(type));

    if (!hasPageType && isUrlNetworkKnown) {
      return buildPagePath(networkId, APP_TO_DEFAULT_PAGE_MAP[app]);
    }

    // Case 3: Tab switch between revenue and insights tabs — current network isn't in the target app's list,
    // so redirect to the first available network for that app.
    if (!selectedNetwork?.id && isUrlNetworkKnown) {
      const targetAppNetworks = allNetworks[app];
      if (targetAppNetworks && targetAppNetworks.length > 0) {
        return buildPagePath(targetAppNetworks[0].id, page);
      }
    }

    return null;
  }, [
    networkId,
    allNetworks,
    isPending,
    isLoading,
    pathname,
    app,
    page,
    selectedNetwork?.id,
    isUrlNetworkKnown,
    defaultNetworkId,
    isQueryMode,
    networksParam,
    searchParams,
    networks,
    buildPagePath,
  ]);

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#f8f8fc]">
        <LocaleProvider locale="en-US">
          <Loader size={4} />
        </LocaleProvider>
      </div>
    );
  }

  // Handle redirects
  if (redirectTarget) {
    return <Navigate to={redirectTarget} replace />;
  }

  // Root route with no networks - show no access. Query mode never has a `networkId`, so this
  // path-only guard must not fire there (its empty-selection case is handled below).
  if (!isQueryMode && !networkId && allNetworks) {
    return <NoAccessMessage />;
  }

  return (
    <DerivedNetworkProvider selectedNetwork={selectedNetwork} selectedNetworks={selectedNetworks}>
      <SubNavigation networkId={navigationNetworkId} />
      <Container>
        {/* If there are no networks, show the no access message */}
        {!selectedNetwork?.id ? (
          <NoAccessMessage hasNetworks={Boolean(networks?.length)} />
        ) : (
          <PageErrorBoundary key={pathname} networks={networks}>
            <Outlet />
          </PageErrorBoundary>
        )}
      </Container>
    </DerivedNetworkProvider>
  );
};

export default PageWrapper;
